/**
 * Shared types and constants for schedule views (List, Board, Gantt, Calendar).
 *
 * These were previously inlined in ProjectSchedulePage.tsx and duplicated
 * across CallbacksPage, HomeServicePage, MultiProjectGanttPage, etc.
 * Centralising them here lets every view organism and page import from one
 * canonical source.
 */

// ── Core types ──────────────────────────────────────────────────────────────

export interface ScheduleTask {
  id: string
  title: string
  status: string
  priority: string
  schedule_type: string   // 'task' | 'phase' | 'milestone' | 'decision'
  phase_id: string | null
  parent_task_id: string | null
  gantt_order: number
  subtask_sort_order: number
  start_date: string | null
  due_date: string | null
  /** Workday-minutes value — same field the task peek's Duration input reads/writes. */
  duration_minutes?: number | null
  depends_on: string[]
  milestone: boolean
  progress: number
  assignee_id: string | null
  assignee_name: string | null
  /** All assignees (users + contacts). Primary stays in assignee_name. */
  assignee_names?: string[] | null
  /** Sign-encoded id source for the canonical multi AssigneePicker (users only). */
  assignee_ids?: number[] | null
  contact_assignee_ids?: number[] | null
  contact_assignee_names?: string[] | null
  // Populated only in global (all-projects) mode
  project_id?: number | null
  project_name?: string | null
  type?: string
  location_id?: number | null
  location_name?: string | null
  // Finish-Schedule room assignment (decision_folders) — drives the Room column.
  room_folder_id?: string | null
  room_folder_name?: string | null
  // Polymorphic linkage (e.g. entity_type='permit' for inspection tasks)
  entity_type?: string | null
  entity_id?: number | null
  inspection_outcome?: string | null
  // Material readiness — see FEAT-TASKS-MATERIALS-STATUS-COLUMN.
  // null = ungroomed; 'none' = manual: not needed; 'required' = materials
  // added, none sourced; 'sourcing' = mid-sourcing; 'ready' = all sourced.
  material_status?: MaterialStatus | null
  // Optimistic-concurrency token (epic #800, #2730) — echoed back as
  // expected_updated_at on guarded writes (dates/status/assignees) so the
  // schedule views 409 instead of silently clobbering a concurrent edit.
  updated_at?: string
  // Flag data — present when task has an open flag (#2973)
  flag?: TaskFlag | null
}

export interface TaskFlag {
  id: number
  reason: string
  note: string
  flagged_by_name?: string
  flagged_at: string
  resolved_at?: string | null
}

export type MaterialStatus = 'none' | 'required' | 'sourcing' | 'ready'

export const MATERIAL_STATUS_LABEL: Record<MaterialStatus, string> = {
  none:     'Not needed',
  required: 'Needs materials',
  sourcing: 'Sourcing',
  ready:    'Ready',
}

export const MATERIAL_STATUS_ORDER: MaterialStatus[] = ['required', 'sourcing', 'ready', 'none']

export interface DepEdge {
  id: number
  source_task_id: string
  target_task_id: string
  dependency_type: string
  lag_days: number
  project_id?: number
}

// ── Status display order (Board view columns, sort helpers) ─────────────────
export const STATUS_ORDER = ["Not Started", "In Progress", "Blocked", "Completed", "Cancelled"]

// ── Status colours — CSS vars (theme-aware, R1.1 compliant) ────────────────
export const STATUS_COLORS: Record<string, string> = {
  "Not Started": "var(--status-not-started-dot)",
  "In Progress": "var(--status-in-progress-dot)",
  "Blocked":     "var(--status-blocked-dot)",
  "Completed":   "var(--status-completed-dot)",
  "Cancelled":   "var(--status-cancelled-dot)",
}

// ── Icon helpers ────────────────────────────────────────────────────────────
export const MILESTONE_ICON = "◆"
export const DECISION_ICON  = "⬡"
export const PHASE_ICON     = "▸"
